// The app: takes a booted context (real, or mock in dev builds), routes, and keeps the hold banner on top.
import { useEffect, useMemo, useState } from "react";
import { PRODUCT_NAME } from "../brand";
import { needsCount } from "../lib/model";
import { createStore } from "../lib/store";
import { MOUNTED_FLAG } from "../boot/fallback";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { HoldBanner } from "../components/HoldBanner";
import { BrokenLink } from "../screens/BrokenLink";
import { CardScreen } from "../screens/CardScreen";
import { Connections } from "../screens/Connections";
import { Inbox } from "../screens/Inbox";
import { MessageAgent } from "../screens/MessageAgent";
import { Pair } from "../screens/Pair";
import { Recent } from "../screens/Recent";
import { Welcome } from "../screens/Welcome";
import { AppContext, useAppState, type AppCtx } from "./AppContext";
import { Controller, idbStorage, relayChannelFactory } from "./controller";
import { realPairRunner } from "./pairRunners";
import { pathFor, useRoute, type Route } from "./router";

/** The real app: device keys in IndexedDB, envelopes over the relay. Mock mode is built in src/mock/boot.ts (dev only). */
export function makeRealApp(): AppCtx {
  const controller = new Controller({ store: createStore(), storage: idbStorage, channelFactory: relayChannelFactory });
  return { controller, mock: null, pairRunner: realPairRunner(controller) };
}

export function App({ ctx }: { ctx?: AppCtx }) {
  const app = useMemo(() => ctx ?? makeRealApp(), [ctx]);
  const DevPanel = app.devPanel;
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    // Tells the inline boot fallback (index.html) the app started. Set after the first commit, not after render(): a
    // crash while drawing the first screen must still let the fallback show (security review 2, S6).
    (window as unknown as Record<string, unknown>)[MOUNTED_FLAG] = true;
  }, []);
  useEffect(() => {
    let alive = true;
    void app.controller.boot().then(() => alive && setBooted(true));
    const onVis = () => {
      if (!document.hidden) {
        app.controller.refresh();
        void app.controller.refreshPushHealth();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
      app.controller.destroy();
    };
  }, [app]);
  const route = useRoute();
  return (
    <AppContext.Provider value={app}>
      <div className="shell">
        {booted ? (
          <ErrorBoundary resetKey={pathFor(route)} fallback={(reset) => <BrokenLink onHome={reset} />}>
            <Routes route={route} />
          </ErrorBoundary>
        ) : (
          <Booting />
        )}
      </div>
      {booted && <HoldBanner />}
      {booted && DevPanel && <DevPanel />}
    </AppContext.Provider>
  );
}

function Booting() {
  return (
    <main className="page no-tabs" aria-busy="true">
      <p className="visually-hidden">Loading {PRODUCT_NAME}</p>
    </main>
  );
}

function Routes({ route }: { route: Route }) {
  const s = useAppState();
  useEffect(() => {
    const el = document.documentElement;
    if (s.prefs.theme === "system") el.removeAttribute("data-theme");
    else el.setAttribute("data-theme", s.prefs.theme);
  }, [s.prefs.theme]);
  useEffect(() => {
    const needs = needsCount(s.cards, s.sends);
    document.title = needs ? `(${needs}) ${PRODUCT_NAME}` : PRODUCT_NAME;
  }, [s.cards, s.sends]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [route]);

  if (route.name === "pair") return <Pair hash={route.hash} />;
  if (route.name === "broken") return <BrokenLink />;
  if (!s.pairing) return <Welcome />;
  switch (route.name) {
    case "inbox":
      return <Inbox />;
    case "card":
      return <CardScreen requestId={route.requestId} />;
    case "recent":
      return <Recent />;
    case "connections":
      return <Connections />;
    case "message":
      return <MessageAgent />;
    default:
      return <Inbox />;
  }
}
